import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { apiCall } from "utils/apiClient";
import ServiceImagesSection from "./components/ServiceImagesSection";
import VideoLinksSection from "./components/VideoLinksSection";
import ServiceBannersSection from "./components/ServiceBannersSection";

const BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL || "";

const resolveMediaUrl = (value) => {
  if (!value) return "";
  const path = String(value);
  if (/^https?:\/\//i.test(path)) return path;
  const base = BASE_URL.replace(/\/+$/, "");
  const normalizedPath = path.replace(/^\/+/, "");
  return base ? `${base}/${normalizedPath}` : normalizedPath;
};

const normalizeGalleryItems = (items, fallbackType) => {
  const safeItems = Array.isArray(items) ? items : [];
  return safeItems.map((item, index) => ({
    id: String(item?.id ?? `${fallbackType}-${index}`),
    url: resolveMediaUrl(
      item?.media_url || item?.url || item?.mediaUrl || item?.thumbnail_url
    ),
    type: item?.media_type || fallbackType,
    title: item?.title || "",
    description: item?.description || "",
    thumbnail_url: item?.thumbnail_url || "",
    sort_order: item?.sort_order || 1,
    is_active: item?.is_active ?? 1,
  }));
};
const DEFAULT_IMAGE_TITLE = "Service Images";
const DEFAULT_IMAGE_DESCRIPTION = "Gallery images";
const DEFAULT_VIDEO_TITLE = "Service Video";
const DEFAULT_TUTORIAL_TITLE = "Tutorial Video";
const DEFAULT_TUTORIAL_DESCRIPTION = "Tutorial video";
const BANNER_DIMENSIONS = {
  desktop: { width: 1920, height: 300 },
  mobile: { width: 420, height: 420 },
};

function ServiceGalleryView() {
  const { serviceId } = useParams();

  const [serviceName, setServiceName] = useState("");
  const [activeTab, setActiveTab] = useState("images");
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [tutorialVideos, setTutorialVideos] = useState([]);
  const [desktopBanners, setDesktopBanners] = useState([]);
  const [mobileBanners, setMobileBanners] = useState([]);
  const [newVideo, setNewVideo] = useState("");
  const [newTutorialVideo, setNewTutorialVideo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [editVideoId, setEditVideoId] = useState(null);
  const [editVideoUrl, setEditVideoUrl] = useState("");
  const [editTutorialVideoId, setEditTutorialVideoId] = useState(null);
  const [editTutorialVideoUrl, setEditTutorialVideoUrl] = useState("");
  const [replaceImageId, setReplaceImageId] = useState(null);
  const [replaceBannerId, setReplaceBannerId] = useState(null);
  const [bannerDeviceType, setBannerDeviceType] = useState("desktop");

  const replaceInputRef = useRef(null);
  const replaceBannerInputRef = useRef(null);

  const validateBannerDimensions = (file, deviceType = "desktop") =>
    new Promise((resolve, reject) => {
      const required = BANNER_DIMENSIONS[deviceType] || BANNER_DIMENSIONS.desktop;
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const isValid =
          image.naturalWidth === required.width && image.naturalHeight === required.height;
        if (isValid) {
          resolve(true);
          return;
        }
        reject(
          new Error(
            `${deviceType === "mobile" ? "Mobile" : "Desktop"} banner size must be ${
              required.width
            }x${required.height}px. Selected image is ${image.naturalWidth}x${image.naturalHeight}px.`
          )
        );
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Unable to read image dimensions. Please select a valid image file."));
      };

      image.src = objectUrl;
    });

  const fetchServiceName = async () => {
    if (!serviceId) return;
    try {
      const res = await apiCall({
        endpoint: `services/${serviceId}`,
        method: "GET",
      });
      if (res?.status === 200) {
        setServiceName(res.data?.name || res.data?.service_name || "");
      }
    } catch (err) {
      console.error("Failed to load service name", err);
    }
  };

  const fetchGalleryItems = async (mediaType) => {
    if (!serviceId) return [];
    try {
      const res = await apiCall({
        endpoint: "service-gallery",
        method: "GET",
        params: {
          service_id: String(serviceId),
          media_type: mediaType,
          is_active: "1",
        },
      });

      if (res?.status === 200) {
        const raw = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
        return normalizeGalleryItems(raw, mediaType);
      }
    } catch (err) {
      console.error(`Failed to load ${mediaType} gallery items`, err);
    }
    return null;
  };

  const fetchServiceBanners = async () => {
    if (!serviceId) return null;
    try {
      const res = await apiCall({
        endpoint: `banner/by-service/${serviceId}`,
        method: "GET",
      });

      const raw = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : [];

      const desktop = [];
      const mobile = [];

      raw.forEach((item, index) => {
        const bannerObj = {
          id: item?.id ?? `${serviceId}-${index}`,
          path: item?.path || item?.image || item?.url || "",
          url: resolveMediaUrl(item?.path || item?.image || item?.url || ""),
          image_index:
            Number(item?.image_index) ??
            Number(item?.index) ??
            Number(item?.sort_order) ??
            0,
          // links: item?.link ? [item.link] : [],
        };

        if (Number(item?.device) === 1) desktop.push(bannerObj);
        else mobile.push(bannerObj);
      });

      // Sort banners by image_index
      desktop.sort((a, b) => (a.image_index ?? 0) - (b.image_index ?? 0));
      mobile.sort((a, b) => (a.image_index ?? 0) - (b.image_index ?? 0));

      return { desktop, mobile };
    } catch (err) {
      console.error("Failed to load service banners", err);
    }
    return null;
  };

  const loadMedia = async () => {
    setIsLoading(true);
    try {
      const [imageItems, videoItems, tutorialItems, bannerResult] =
        await Promise.all([
          fetchGalleryItems("image"),
          fetchGalleryItems("video"),
          fetchGalleryItems("tutorial"),
          fetchServiceBanners(),
        ]);

      const allFailed =
        imageItems === null &&
        videoItems === null &&
        tutorialItems === null &&
        bannerResult === null;

      if (allFailed) {
        setImages([]);
        setVideos([]);
        setTutorialVideos([]);
        setDesktopBanners([]);
        setMobileBanners([]);
      } else {
        // Defensive filtering: some backends ignore media_type and return
        // mixed results. Only keep items where `type` matches expected.
        const onlyImages = (imageItems || []).filter((it) => it?.type === "image");
        const onlyVideos = (videoItems || []).filter((it) => it?.type === "video");
        const onlyTutorials = (tutorialItems || []).filter((it) => it?.type === "tutorial");

        setImages(onlyImages);
        setVideos(onlyVideos);
        setTutorialVideos(onlyTutorials);
        setDesktopBanners(bannerResult?.desktop || []);
        setMobileBanners(bannerResult?.mobile || []);
      }

      fetchServiceName();
    } catch (err) {
      console.error("Failed to load service media", err);
      setImages([]);
      setVideos([]);
      setTutorialVideos([]);
      setDesktopBanners([]);
      setMobileBanners([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMedia();
  }, [serviceId]);

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach((f) => formData.append("images[]", f));
    formData.append("service_id", String(serviceId));
    formData.append("title", DEFAULT_IMAGE_TITLE);
    formData.append("description", DEFAULT_IMAGE_DESCRIPTION);
    formData.append("sort_order", "1");

    try {
      await apiCall({
        endpoint: "service-gallery/upload-images",
        method: "POST",
        data: formData,
      });
      loadMedia();
    } catch (err) {
      console.error("Failed to upload images", err);
    }
  };

  const uploadBanners = async (e, deviceType = "desktop", customIndex = "") => {
    const file = e.target.files?.[0];
    if (!file) return;

    let nextIndex = customIndex ? Number(customIndex) : null;
    
    if (!nextIndex || nextIndex <= 0) {
      const targetBanners = deviceType === "mobile" ? mobileBanners : desktopBanners;
      
      // Get max index from existing banners
      let maxIndex = 0;
      targetBanners.forEach((banner) => {
        const idx = Number(banner?.image_index);
        if (!isNaN(idx) && idx > 0) {
          maxIndex = Math.max(maxIndex, idx);
        }
      });
      
      // If no banners have valid indices, use count + 1
      if (maxIndex === 0) {
        maxIndex = targetBanners.length;
      }
      
      nextIndex = maxIndex + 1;
    }

    try {
      await validateBannerDimensions(file, deviceType);
      const formData = new FormData();
      formData.append("path", file);
      formData.append("service_id", String(serviceId));
      formData.append("image_index", String(nextIndex));
      formData.append("device", deviceType === "mobile" ? "2" : "1");

      await apiCall({
        endpoint: "/banner/createBannerImage",
        method: "POST",
        data: formData,
      });
      loadMedia();
    } catch (err) {
      if (err?.message) window.alert(err.message);
      console.error("Failed to upload banners", err);
    } finally {
      e.target.value = "";
    }
  };

  const addVideo = async () => {
    if (!newVideo.trim()) return;

    try {
      const payload = new URLSearchParams({
        service_id: String(serviceId),
        media_url: newVideo.trim(),
        title: DEFAULT_VIDEO_TITLE,
        thumbnail_url: "",
      });
      await apiCall({
        endpoint: "service-gallery/add-video",
        method: "POST",
        data: payload,
      });
      setNewVideo("");
      loadMedia();
    } catch (err) {
      console.error("Failed to add video", err);
    }
  };

  const addTutorialVideo = async () => {
    if (!newTutorialVideo.trim()) return;

    try {
      const payload = new URLSearchParams({
        service_id: String(serviceId),
        media_url: newTutorialVideo.trim(),
        title: DEFAULT_TUTORIAL_TITLE,
        description: DEFAULT_TUTORIAL_DESCRIPTION,
      });
      await apiCall({
        endpoint: "service-gallery/add-tutorial-video",
        method: "POST",
        data: payload,
      });
      setNewTutorialVideo("");
      loadMedia();
    } catch (err) {
      console.error("Failed to add tutorial video", err);
    }
  };

  const deleteImageById = async (id) => {
    await apiCall({
      endpoint: `service-gallery/delete-image/${id}`,
      method: "DELETE",
    });
  };

  const deleteVideoById = async (id, type = "") => {
    const endpoint =
      type === "tutorial"
        ? `service-gallery/delete-tutorial-video/${id}`
        : `service-gallery/delete-video/${id}`;
    await apiCall({
      endpoint,
      method: "DELETE",
    });
  };

  const deleteImage = async (id) => {
    if (!window.confirm("Remove this image?")) return;
    try {
      await deleteImageById(id);
      loadMedia();
    } catch (err) {
      console.error("Failed to delete image", err);
    }
  };

  const deleteBanner = async (id) => {
    if (!window.confirm("Remove this banner?")) return;
    try {
      await apiCall({
        endpoint: `/banner/deleteBanner/${id}`,
        method: "DELETE",
      });
      loadMedia();
    } catch (err) {
      console.error("Failed to delete banner", err);
    }
  };

  const deleteVideo = async (id) => {
    if (!window.confirm("Remove this video?")) return;
    try {
      await deleteVideoById(id);
      loadMedia();
    } catch (err) {
      console.error("Failed to delete video", err);
    }
  };

  const deleteTutorialVideo = async (id) => {
    if (!window.confirm("Remove this tutorial video?")) return;
    try {
      await deleteVideoById(id, "tutorial");
      loadMedia();
    } catch (err) {
      console.error("Failed to delete tutorial video", err);
    }
  };

  const startEditVideo = (video) => {
    setEditVideoId(video.id);
    setEditVideoUrl(video.url || "");
  };

  const startEditTutorialVideo = (video) => {
    setEditTutorialVideoId(video.id);
    setEditTutorialVideoUrl(video.url || "");
  };

  const cancelEditVideo = () => {
    setEditVideoId(null);
    setEditVideoUrl("");
  };

  const cancelEditTutorialVideo = () => {
    setEditTutorialVideoId(null);
    setEditTutorialVideoUrl("");
  };

  const saveEditVideo = async () => {
    const nextUrl = editVideoUrl.trim();
    if (!nextUrl || !editVideoId) return;
    if (!window.confirm("Replace this video URL?")) return;

    const current = videos.find((video) => video.id === editVideoId);
    const payload = {
      title: current?.title || DEFAULT_VIDEO_TITLE,
      description: current?.description || "",
      media_url: nextUrl,
      thumbnail_url: current?.thumbnail_url || "",
      sort_order: current?.sort_order || 1,
    };

    try {
      await apiCall({
        endpoint: `service-gallery/update-video/${editVideoId}`,
        method: "PUT",
        data: payload,
      });
      cancelEditVideo();
      loadMedia();
    } catch (err) {
      console.error("Failed to update video", err);
    }
  };

  const saveEditTutorialVideo = async () => {
    const nextUrl = editTutorialVideoUrl.trim();
    if (!nextUrl || !editTutorialVideoId) return;
    if (!window.confirm("Replace this tutorial video URL?")) return;

    const current = tutorialVideos.find(
      (video) => video.id === editTutorialVideoId
    );
    const payload = {
      title: current?.title || DEFAULT_TUTORIAL_TITLE,
      description:
        current?.description || DEFAULT_TUTORIAL_DESCRIPTION,
      media_url: nextUrl,
      sort_order: current?.sort_order || 1,
    };

    try {
      await apiCall({
        endpoint: `service-gallery/update-tutorial-video/${editTutorialVideoId}`,
        method: "PUT",
        data: payload,
      });
      cancelEditTutorialVideo();
      loadMedia();
    } catch (err) {
      console.error("Failed to update tutorial video", err);
    }
  };
  const handleReplaceImageClick = (id) => {
    setReplaceImageId(id);
    if (replaceInputRef.current) {
      replaceInputRef.current.value = "";
      replaceInputRef.current.click();
    }
  };

  const replaceImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replaceImageId) return;
    if (!window.confirm("Replace this image?")) return;

    const formData = new FormData();
    formData.append("images[]", file);
    formData.append("service_id", String(serviceId));
    formData.append("title", DEFAULT_IMAGE_TITLE);
    formData.append("description", DEFAULT_IMAGE_DESCRIPTION);
    formData.append("sort_order", "1");

    try {
      const res = await apiCall({
        endpoint: "service-gallery/upload-images",
        method: "POST",
        data: formData,
      });
      if (res?.status === 200 || res?.status === 201 || res?.success) {
        await deleteImageById(replaceImageId);
      }
      loadMedia();
    } catch (err) {
      console.error("Failed to replace image", err);
    } finally {
      setReplaceImageId(null);
    }
  };

  const handleReplaceBannerClick = (id, deviceType) => {
    setReplaceBannerId(id);
    setBannerDeviceType(deviceType);
    if (replaceBannerInputRef.current) {
      replaceBannerInputRef.current.value = "";
      replaceBannerInputRef.current.click();
    }
  };

  const replaceBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replaceBannerId) return;
    if (!window.confirm("Replace this banner?")) return;

    const currentList = bannerDeviceType === "mobile" ? mobileBanners : desktopBanners;
    const currentBanner = currentList.find((item) => item.id === replaceBannerId);
    const nextIndex = Number(currentBanner?.image_index) || 1;

    const formData = new FormData();
    formData.append("path", file);
    formData.append("service_id", String(serviceId));
    formData.append("image_index", String(nextIndex));
    formData.append("device", bannerDeviceType === "mobile" ? "2" : "1");

    try {
      await validateBannerDimensions(file, bannerDeviceType);
      const res = await apiCall({
        endpoint: "/banner/createBannerImage",
        method: "POST",
        data: formData,
      });
      if (res?.status === 200 || res?.status === 201 || res?.success) {
        await apiCall({
          endpoint: `/banner/deleteBanner/${replaceBannerId}`,
          method: "DELETE",
        });
      }
      loadMedia();
    } catch (err) {
      if (err?.message) window.alert(err.message);
      console.error("Failed to replace banner", err);
    } finally {
      setReplaceBannerId(null);
      setBannerDeviceType("desktop");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="p-6 space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold">
              {serviceName || "Service Gallery"}
            </h2>
            <p className="text-sm text-gray-500">
              Manage images and videos for this service.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${activeTab === "images"
                  ? "bg-sky-600 text-white"
                  : "border text-gray-600 hover:bg-gray-50"
                }`}
              onClick={() => setActiveTab("images")}
            >
              Images
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                {images.length}
              </span>
            </button>
            <button
              type="button"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${activeTab === "videos"
                  ? "bg-indigo-600 text-white"
                  : "border text-gray-600 hover:bg-gray-50"
                }`}
              onClick={() => setActiveTab("videos")}
            >
              Videos
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                {videos.length}
              </span>
            </button>
            <button
              type="button"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${activeTab === "tutorial"
                  ? "bg-emerald-600 text-white"
                  : "border text-gray-600 hover:bg-gray-50"
                }`}
              onClick={() => setActiveTab("tutorial")}
            >
              Tutorial Video
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                {tutorialVideos.length}
              </span>
            </button>
            <button
              type="button"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${activeTab === "banners"
                  ? "bg-purple-600 text-white"
                  : "border text-gray-600 hover:bg-gray-50"
                }`}
              onClick={() => setActiveTab("banners")}
            >
              Service Banners
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                {desktopBanners.length + mobileBanners.length}
              </span>
            </button>
          </div>
        </div>

        {isLoading ? (
          <p className="text-center text-gray-500">
            Loading media...
          </p>
        ) : (
          <>
            {activeTab === "images" && (
              <ServiceImagesSection
                images={images}
                uploadImages={uploadImages}
                replaceInputRef={replaceInputRef}
                replaceImage={replaceImage}
                handleReplaceImageClick={handleReplaceImageClick}
                deleteImage={deleteImage}
              />
            )}

            {activeTab === "videos" && (
              <VideoLinksSection
                title="Videos"
                description="Add video URLs and manage them here."
                inputValue={newVideo}
                onInputChange={setNewVideo}
                inputPlaceholder="Paste video URL"
                onAdd={addVideo}
                addButtonLabel="Add Video"
                addButtonClassName="bg-indigo-600 hover:bg-indigo-700"
                items={videos}
                emptyText="No videos added yet."
                editItemId={editVideoId}
                editItemUrl={editVideoUrl}
                onEditItemUrlChange={setEditVideoUrl}
                editPlaceholder="Update video URL"
                onSaveEdit={saveEditVideo}
                onCancelEdit={cancelEditVideo}
                onStartEdit={startEditVideo}
                onDelete={deleteVideo}
              />
            )}

            {activeTab === "tutorial" && (
              <VideoLinksSection
                title="Tutorial Videos"
                description="Add tutorial video URLs and manage them here."
                inputValue={newTutorialVideo}
                onInputChange={setNewTutorialVideo}
                inputPlaceholder="Paste tutorial video URL"
                onAdd={addTutorialVideo}
                addButtonLabel="Add Tutorial Video"
                addButtonClassName="bg-emerald-600 hover:bg-emerald-700"
                items={tutorialVideos}
                emptyText="No tutorial videos added yet."
                editItemId={editTutorialVideoId}
                editItemUrl={editTutorialVideoUrl}
                onEditItemUrlChange={setEditTutorialVideoUrl}
                editPlaceholder="Update tutorial video URL"
                onSaveEdit={saveEditTutorialVideo}
                onCancelEdit={cancelEditTutorialVideo}
                onStartEdit={startEditTutorialVideo}
                onDelete={deleteTutorialVideo}
              />
            )}

            {activeTab === "banners" && (
              <ServiceBannersSection
                desktopBanners={desktopBanners}
                mobileBanners={mobileBanners}
                handleReplaceBannerClick={handleReplaceBannerClick}
                deleteBanner={deleteBanner}
                uploadBanners={uploadBanners}
                replaceBannerInputRef={replaceBannerInputRef}
                replaceBanner={replaceBanner}
              />
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

export default ServiceGalleryView;
